import { Moon, Sun } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

type Stats = {
	total: number;
	active: number;
	delisted: number;
	marketValue: number;
	byType: { type: string; count: number }[];
};
type RecentRow = {
	id: number;
	name: string | null;
	property_type: string | null;
	size: number | null;
	total_price: number | null;
	price_per_sq_m: number | null;
	first_seen: string;
	url: string;
};
type PriceChangeRow = {
	id: number;
	name: string | null;
	property_type: string | null;
	old_price: number;
	new_price: number;
	recorded_at: string;
	url: string;
};
type SellerRow = {
	seller: string;
	seller_url: string | null;
	count: number;
	total_value: number;
};
type ViewedRow = {
	id: number;
	name: string | null;
	property_type: string | null;
	total_price: number | null;
	views: number;
	url: string;
};
type DelistedRow = {
	id: number;
	name: string | null;
	property_type: string | null;
	size: number | null;
	total_price: number | null;
	first_seen: string;
	delisted_at: string;
	url: string;
};

const eur = new Intl.NumberFormat("sl-SI", {
	style: "currency",
	currency: "EUR",
	maximumFractionDigits: 0,
});
const num = new Intl.NumberFormat("sl-SI");

function compactEur(v: number): string {
	if (v >= 1e9) return `€${(v / 1e9).toFixed(1)} mrd`;
	if (v >= 1e6) return `€${(v / 1e6).toFixed(1)} mio`;
	return eur.format(v);
}
function money(v: number | null): string {
	return v == null ? "—" : eur.format(v);
}
function area(v: number | null): string {
	return v == null ? "—" : `${num.format(v)} m²`;
}
function when(iso: string): string {
	const d = new Date(iso);
	return d.toLocaleString("sl-SI", {
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function StatCard({ label, value }: { label: string; value: string }) {
	return (
		<div className="stat-card">
			<div className="label">{label}</div>
			<div className="value">{value}</div>
		</div>
	);
}

function StatCards({ s }: { s: Stats }) {
	return (
		<div className="stat-cards">
			<StatCard label="Aktivnih" value={num.format(s.active)} />
			<StatCard label="Skupna vrednost" value={compactEur(s.marketValue)} />
			{s.byType.slice(0, 4).map((t) => (
				<StatCard key={t.type} label={t.type} value={num.format(t.count)} />
			))}
		</div>
	);
}

function StatCardsSkeleton() {
	return (
		<div className="stat-cards">
			{Array.from({ length: 6 }).map((_, i) => (
				<div className="stat-card" key={i}>
					<div className="label">
						<span className="skel" />
					</div>
					<div className="value">
						<span className="skel" />
					</div>
				</div>
			))}
		</div>
	);
}

type Col = { key: string; label: string; className?: string };

function Table({
	title,
	cols,
	rows,
	render,
	loading,
}: {
	title: string;
	cols: Col[];
	rows: Record<string, unknown>[];
	render: (row: Record<string, unknown>, key: string) => React.ReactNode;
	loading?: boolean;
}) {
	return (
		<>
			<h2>{title}</h2>
			<div className="table-scroll">
			<table className="country-table">
				<thead>
					<tr>
						<th className="rank">#</th>
						{cols.map((c) => (
							<th key={c.key} className={c.className}>
								{c.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{loading &&
						Array.from({ length: 8 }).map((_, i) => (
							<tr key={`s${i}`}>
								<td className="rank">
									<span className="skel" />
								</td>
								{cols.map((c) => (
									<td key={c.key} className={c.className}>
										<span className="skel" />
									</td>
								))}
							</tr>
						))}
					{!loading && rows.length === 0 && (
						<tr>
							<td colSpan={cols.length + 1} className="empty">
								Ni podatkov
							</td>
						</tr>
					)}
					{!loading &&
						rows.map((row, i) => (
						<tr key={(row.id as number) ?? i}>
							<td className="rank">{i + 1}</td>
							{cols.map((c) => (
								<td key={c.key} className={c.className}>
									{render(row, c.key)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
			</div>
		</>
	);
}

function NameLink({ row }: { row: { id: number; name: string | null } }) {
	return (
		<a href={`/go/${row.id}`} target="_blank" rel="noreferrer" className="name-link">
			{row.name ?? "(brez naziva)"}
		</a>
	);
}

type RegionHist = { region: string; counts: number[]; median: number; count: number };
type RegionHistograms = { bins: number; binWidth: number; upper: number; regions: RegionHist[] };

// Small €/m² distribution chart for one region — same look as the map filter
// histograms (.histogram-filter > .hist-bars > gradient .hist-bar).
function MiniHistogram({ r, upper, binWidth }: { r: RegionHist; upper: number; binWidth: number }) {
	const max = Math.max(1, ...r.counts);
	const n = r.counts.length;
	const medPct = Math.min(100, Math.max(0, (r.median / upper) * 100));
	return (
		<div className="region-card">
			<div className="region-head">
				<span className="region-name">{r.region}</span>
			</div>
			<div className="histogram-filter">
				<div className="hist-bars">
					<div
						className={`region-med${medPct > 80 ? " is-right" : ""}`}
						style={{ left: `${medPct}%` }}
					>
						<span className="region-med-val">{num.format(Math.round(r.median))} €/m²</span>
					</div>
					{r.counts.map((c, i) => {
						const pct = n > 1 ? i / (n - 1) : 0;
						const style = {
							height: `${Math.max(6, (c / max) * 100)}%`,
							"--bar-color": `hsl(${24 + pct * 110} 68% 58%)`,
						} as React.CSSProperties;
						return (
							<span
								key={i}
								className="hist-bar is-active"
								style={style}
								title={`${num.format(Math.round(i * binWidth))}–${num.format(Math.round((i + 1) * binWidth))} €/m² · ${num.format(c)}`}
							/>
						);
					})}
				</div>
				<div className="hist-scale">
					<span>0</span>
					<span>{num.format(r.count)} oglasov</span>
					<span>{num.format(upper)}+</span>
				</div>
			</div>
		</div>
	);
}

function RegionHistogramsSection({ data }: { data: RegionHistograms }) {
	return (
		<>
			<h2>Cena na m² po regijah (Slovenija)</h2>
			<div className="region-grid">
				{data.regions.map((r) => (
					<MiniHistogram key={r.region} r={r} upper={data.upper} binWidth={data.binWidth} />
				))}
			</div>
		</>
	);
}

function App() {
	const [stats, setStats] = useState<Stats | null>(null);
	const [hist, setHist] = useState<RegionHistograms | null>(null);
	const [recent, setRecent] = useState<RecentRow[]>([]);
	const [changes, setChanges] = useState<PriceChangeRow[]>([]);
	const [sellers, setSellers] = useState<SellerRow[]>([]);
	const [viewed, setViewed] = useState<ViewedRow[]>([]);
	const [delisted, setDelisted] = useState<DelistedRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [err, setErr] = useState<string | null>(null);
	const [dark, setDark] = useState(
		typeof localStorage !== "undefined" &&
			localStorage.getItem("n10e-dark") === "1",
	);

	useEffect(() => {
		document.documentElement.classList.toggle("dark", dark);
		localStorage.setItem("n10e-dark", dark ? "1" : "0");
	}, [dark]);

	useEffect(() => {
		Promise.all([
			fetch("/api/stats").then((r) => r.json()),
			fetch("/api/recent").then((r) => r.json()),
			fetch("/api/price-changes").then((r) => r.json()),
			fetch("/api/top-sellers").then((r) => r.json()),
			fetch("/api/most-viewed").then((r) => r.json()),
			fetch("/api/delisted").then((r) => r.json()),
		])
			.then(([s, r, c, sl, v, d]) => {
				setStats(s);
				setRecent(r);
				setChanges(c);
				setSellers(sl);
				setViewed(v);
				setDelisted(d);
			})
			.catch((e) => setErr(String(e)))
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		fetch("/api/region-histograms")
			.then((r) => r.json())
			.then(setHist)
			.catch(() => {});
	}, []);

	return (
		<>
			<nav className="nav-header">
				<a href="/" className="logo">
					n10e
				</a>
				<div className="nav-links">
					<a href="/" className="active">
						Statistika
					</a>
					<a href="/map">Zemljevid</a>
					<a href="/cene">Cene</a>
				</div>
				<button
					className="dark-toggle"
					onClick={() => setDark((d) => !d)}
					type="button"
					aria-label={dark ? "Svetla tema" : "Temna tema"}
				>
					{dark ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
				</button>
			</nav>
			<div className="page">
				<h1>Nepremičnine · statistika</h1>
				<p className="subtitle">Pregled oglasov z nepremicnine.net</p>

				{err && <div className="error">Napaka: {err}</div>}
			{stats ? <StatCards s={stats} /> : <StatCardsSkeleton />}

			{hist && hist.regions.length > 0 && <RegionHistogramsSection data={hist} />}

			<Table
				title="Najnovejši oglasi"
				cols={[
					{ key: "name", label: "Oglas" },
					{ key: "property_type", label: "Tip" },
					{ key: "size", label: "Velikost", className: "num" },
					{ key: "total_price", label: "Cena", className: "num" },
					{ key: "first_seen", label: "Dodano", className: "num" },
				]}
				rows={recent}
				loading={loading}
				render={(row, key) => {
					const r = row as unknown as RecentRow;
					if (key === "name") return <NameLink row={r} />;
					if (key === "property_type") return r.property_type ?? "—";
					if (key === "size") return area(r.size);
					if (key === "total_price") return money(r.total_price);
					if (key === "first_seen") return when(r.first_seen);
					return null;
				}}
			/>

			<Table
				title="Spremembe cen"
				cols={[
					{ key: "name", label: "Oglas" },
					{ key: "property_type", label: "Tip" },
					{ key: "old_price", label: "Stara cena", className: "num" },
					{ key: "new_price", label: "Nova cena", className: "num" },
					{ key: "delta", label: "Sprememba", className: "num" },
					{ key: "recorded_at", label: "Datum", className: "num" },
				]}
				rows={changes}
				loading={loading}
				render={(row, key) => {
					const r = row as unknown as PriceChangeRow;
					if (key === "name") return <NameLink row={r} />;
					if (key === "property_type") return r.property_type ?? "—";
					if (key === "old_price") return money(r.old_price);
					if (key === "new_price") return money(r.new_price);
					if (key === "delta") {
						const pct = ((r.new_price - r.old_price) / r.old_price) * 100;
						const up = r.new_price > r.old_price;
						return (
							<span className={up ? "delta-up" : "delta-down"}>
								{up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
							</span>
						);
					}
					if (key === "recorded_at") return when(r.recorded_at);
					return null;
				}}
			/>

			<Table
				title="Največji ponudniki"
				cols={[
					{ key: "seller", label: "Ponudnik" },
					{ key: "count", label: "Oglasov", className: "num" },
					{ key: "total_value", label: "Skupna vrednost", className: "num" },
				]}
				rows={sellers}
				loading={loading}
				render={(row, key) => {
					const r = row as unknown as SellerRow;
					if (key === "seller")
						return r.seller_url ? (
							<a
								href={r.seller_url}
								target="_blank"
								rel="noreferrer"
								className="name-link"
							>
								{r.seller}
							</a>
						) : (
							r.seller
						);
					if (key === "count") return num.format(r.count);
					if (key === "total_value") return compactEur(r.total_value);
					return null;
				}}
			/>

			<Table
				title="Najbolj ogledani"
				cols={[
					{ key: "name", label: "Oglas" },
					{ key: "property_type", label: "Tip" },
					{ key: "total_price", label: "Cena", className: "num" },
					{ key: "views", label: "Ogledov", className: "num" },
				]}
				rows={viewed}
				loading={loading}
				render={(row, key) => {
					const r = row as unknown as ViewedRow;
					if (key === "name")
						return (
							<a
								href={`/go/${r.id}`}
								target="_blank"
								rel="noreferrer"
								className="name-link"
							>
								{r.name ?? "(brez naziva)"}
							</a>
						);
					if (key === "property_type") return r.property_type ?? "—";
					if (key === "total_price") return money(r.total_price);
					if (key === "views") return num.format(r.views);
					return null;
				}}
			/>

			<Table
				title="Nedavno umaknjeni"
				cols={[
					{ key: "name", label: "Oglas" },
					{ key: "property_type", label: "Tip" },
					{ key: "size", label: "Velikost", className: "num" },
					{ key: "total_price", label: "Zadnja cena", className: "num" },
					{ key: "delisted_at", label: "Umaknjeno", className: "num" },
				]}
				rows={delisted}
				loading={loading}
				render={(row, key) => {
					const r = row as unknown as DelistedRow;
					if (key === "name") return <NameLink row={r} />;
					if (key === "property_type") return r.property_type ?? "—";
					if (key === "size") return area(r.size);
					if (key === "total_price") return money(r.total_price);
					if (key === "delisted_at") return when(r.delisted_at);
					return null;
				}}
				/>
			</div>
		</>
	);
}

const root = createRoot(document.getElementById("root")!);
root.render(<App />);
